import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';

const EditClassModal = ({ isOpen, onClose, classItem, onSave }) => {
  const [formData, setFormData] = useState({
    googleClassroomLink: '',
    zoomLink: '',
    zoomPassword: '',
    schedule: [{ day: '', startTime: '', duration: '' }],
    termData: [{ term: '', termStart: '', termEnd: '' }],
  });
  const [saving, setSaving] = useState(false);

  // Reset form data when modal opens with a new classItem
  useEffect(() => {
    if (isOpen && classItem) {
      setFormData({
        googleClassroomLink: classItem.googleClassroomLink || '',
        zoomLink: classItem.zoomLink || '',
        zoomPassword: classItem.zoomPassword || '',
        schedule: classItem.schedule && classItem.schedule.length > 0
          ? JSON.parse(JSON.stringify(classItem.schedule))
          : [{ day: '', startTime: '', duration: '' }],
        // ✅ If classItem has termData, use it; otherwise start with empty term
        termData: classItem.termData && classItem.termData.length > 0
          ? JSON.parse(JSON.stringify(classItem.termData))
          : [{ term: '', termStart: '', termEnd: '' }],
      });
    }
  }, [isOpen, classItem]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // ---- Term handlers ----
  const addTermRow = () => {
    setFormData(prev => ({
      ...prev,
      termData: [...prev.termData, { term: '', termStart: '', termEnd: '' }]
    }));
  };

  const removeTermRow = (index) => {
    if (formData.termData.length <= 1) {
      alert('You need at least one term.');
      return;
    }
    const newTermData = formData.termData.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, termData: newTermData }));
  };

  const updateTerm = (index, field, value) => {
    const newTermData = [...formData.termData];
    newTermData[index][field] = value;
    setFormData(prev => ({ ...prev, termData: newTermData }));
  };

  // ---- Schedule handlers ----
  const addSession = () => {
    setFormData(prev => ({
      ...prev,
      schedule: [...prev.schedule, { day: '', startTime: '', duration: '' }]
    }));
  };

  const removeSession = (index) => {
    if (formData.schedule.length <= 1) {
      alert('You need at least one session.');
      return;
    }
    const newSchedule = formData.schedule.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, schedule: newSchedule }));
  };

  const updateSession = (index, field, value) => {
    const newSchedule = [...formData.schedule];
    newSchedule[index][field] = value;
    setFormData(prev => ({ ...prev, schedule: newSchedule }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validSessions = formData.schedule.filter(s => s.day && s.startTime && s.duration);
    if (validSessions.length === 0) {
      alert('Please add at least one valid session (day, start time, duration).');
      return;
    }

    // ✅ Filter out empty term rows
    const validTerms = formData.termData.filter(t => t.term && t.termStart && t.termEnd);

    setSaving(true);
    try {
      // ✅ Pass both schedule and termData
      await onSave({
        googleClassroomLink: formData.googleClassroomLink,
        zoomLink: formData.zoomLink,
        zoomPassword: formData.zoomPassword,
        schedule: formData.schedule,
        termData: validTerms, // ✅ Term data to apply to all students
      });
      onClose();
    } catch (err) {
      alert('Failed to update class: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <h3 className="text-xl font-bold mb-4">Edit Class – {classItem?.subject} ({classItem?.grade})</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium">Google Classroom Link</label>
          <input
            type="text"
            value={formData.googleClassroomLink}
            onChange={e => handleChange('googleClassroomLink', e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Zoom Link</label>
          <input
            type="text"
            value={formData.zoomLink}
            onChange={e => handleChange('zoomLink', e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Zoom Password</label>
          <input
            type="text"
            value={formData.zoomPassword}
            onChange={e => handleChange('zoomPassword', e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>

        {/* ===== TERM DATA SECTION ===== */}
        <div className="border-t pt-3">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium">Term Dates (Applied to all students)</h4>
            <button
              type="button"
              onClick={addTermRow}
              className="text-sm bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700"
            >
              + Add Term
            </button>
          </div>
          {formData.termData.map((term, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-2 mb-2 items-end">
              <div>
                <label className="block text-xs font-medium">Term Name</label>
                <input
                  type="text"
                  value={term.term || ''}
                  onChange={e => updateTerm(idx, 'term', e.target.value)}
                  placeholder="e.g., Term 1"
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium">Start Date</label>
                <input
                  type="date"
                  value={term.termStart || ''}
                  onChange={e => updateTerm(idx, 'termStart', e.target.value)}
                  className="w-full border rounded px-2 py-1 text-sm"
                />
              </div>
              <div className="flex items-end gap-1">
                <div className="flex-1">
                  <label className="block text-xs font-medium">End Date</label>
                  <input
                    type="date"
                    value={term.termEnd || ''}
                    onChange={e => updateTerm(idx, 'termEnd', e.target.value)}
                    className="w-full border rounded px-2 py-1 text-sm"
                  />
                </div>
                {formData.termData.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTermRow(idx)}
                    className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                    title="Remove term"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
          {formData.termData.length === 0 && (
            <p className="text-sm text-gray-500">No terms added. Click "+ Add Term" to add one.</p>
          )}
          <p className="text-xs text-gray-400 mt-1">
            Terms set here will be applied to all students in this class (stored in their schedule).
          </p>
        </div>

        {/* ===== SCHEDULE SECTION ===== */}
        <div className="border-t pt-3">
          <div className="flex justify-between items-center mb-2">
            <h4 className="font-medium">Class Sessions</h4>
            <button
              type="button"
              onClick={addSession}
              className="text-sm bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
            >
              + Add Session
            </button>
          </div>
          {formData.schedule.map((session, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-2 mb-2 items-end">
              <div>
                <label className="block text-xs font-medium">Day</label>
                <input
                  type="text"
                  value={session.day || ''}
                  onChange={e => updateSession(idx, 'day', e.target.value)}
                  placeholder="e.g., Monday"
                  className="w-full border rounded px-2 py-1 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium">Start Time</label>
                <input
                  type="text"
                  value={session.startTime || ''}
                  onChange={e => updateSession(idx, 'startTime', e.target.value)}
                  placeholder="e.g., 09:00"
                  className="w-full border rounded px-2 py-1 text-sm"
                  required
                />
              </div>
              <div className="flex items-end gap-1">
                <div className="flex-1">
                  <label className="block text-xs font-medium">Duration (min)</label>
                  <input
                    type="number"
                    value={session.duration || ''}
                    onChange={e => updateSession(idx, 'duration', e.target.value)}
                    placeholder="60"
                    className="w-full border rounded px-2 py-1 text-sm"
                    required
                  />
                </div>
                {formData.schedule.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSession(idx)}
                    className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                    title="Remove session"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          ))}
          {formData.schedule.length === 0 && (
            <p className="text-sm text-gray-500">No sessions added. Click "+ Add Session" to add one.</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditClassModal;